import { useNavigate } from "react-router-dom";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Button } from "@/components/ui/button";
import { Megaphone, Plus, SearchIcon } from "lucide-react";
import { CampaignCard } from "@/components/dashboard_campaigns/campaign-card";
import ArchiveTabs from "@/components/dashboard_campaigns/archive-tabs";
import { useDashboardStore } from "@/stores";
import EmptyState from "@/components/ui/empty-state";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useUser } from "@clerk/react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Campaigns() {
  const { campaigns, campaignsLoading } = useDashboardStore();
  const [searchCampaigns, setSearchCampaigns] = useState<string>("");
  const { user } = useUser();
  const [onlyShowMyCampaigns, setOnlyShowMyCampaigns] = useState<boolean>(
    localStorage.getItem("onlyShowMyCampaigns") === "true"
  );

  return (
    <div className="w-full h-full flex flex-col gap-5 p-5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/30%)_transparent]">
      <div className="flex items-center flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Campaigns</h1>
        <div
          onClick={() => {
            setOnlyShowMyCampaigns(!onlyShowMyCampaigns);
            localStorage.setItem(
              "onlyShowMyCampaigns",
              !onlyShowMyCampaigns ? "true" : "false"
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOnlyShowMyCampaigns(!onlyShowMyCampaigns);
              localStorage.setItem(
                "onlyShowMyCampaigns",
                !onlyShowMyCampaigns ? "true" : "false"
              );
            }
          }}
          role="button"
          tabIndex={0}
          className="border-input ml-auto w-auto has-data-[state=unchecked]:text-muted-foreground relative flex items-center gap-2 rounded-md border p-2 shadow-xs outline-none h-8.75"
        >
          <Checkbox
            id="only_show_my_campaigns"
            className="order-0 after:absolute after:inset-0"
            aria-describedby={`only_show_my_campaigns-description`}
            checked={onlyShowMyCampaigns}
          />
          <span className="text-xs text-nowrap font-medium">My Campaigns</span>
        </div>
        <InputGroup className="w-64">
          <InputGroupInput
            id="campaign-search"
            placeholder="Search campaigns"
            type="search"
            onChange={(e) => setSearchCampaigns(e.target.value)}
          />
          <InputGroupAddon>
            <SearchIcon aria-hidden="true" />
          </InputGroupAddon>
        </InputGroup>
        <ArchiveTabs />
      </div>

      {(!campaignsLoading || campaigns.length != 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 w-full pb-5">
          <>
            <CreateCampaignButton />
            {campaigns
              ?.filter((campaign) =>
                campaign.campaign_name
                  .toLowerCase()
                  .includes(searchCampaigns.toLowerCase())
              )
              .filter((campaign) =>
                onlyShowMyCampaigns ? campaign.owner?.id === user?.id : true
              )
              .map((campaign, index) => (
                <CampaignCard
                  key={index}
                  {...campaign}
                  loading={campaignsLoading}
                />
              ))}
          </>
        </div>
      )}

      {campaignsLoading && campaigns.length == 0 && <Skeletons />}

      {campaigns?.length === 0 && !campaignsLoading && (
        <EmptyState
          icon={<Megaphone />}
          text="Create your first campaign to get started."
          className="size-96"
        />
      )}
    </div>
  );
}

const Skeletons = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 w-full">
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
      <Skeleton className="w-full h-48 rounded-2xl bg-sidebar"/>
    </div>
  );
};

// Concentric dashed rings that spread apart on hover (each with its own
// delay, so they ripple). Every ring sits one --gap inside the last, so its
// radius is the card's minus that many gaps, never below 2px (0 on the
// Square preset): hand-subtracted radii went negative (square) on the default
// theme and stopped matching once hover widened the gap.
const RINGS = [
  { border: "border-border/80", duration: "duration-100" },
  { border: "border-border/70", duration: "duration-300" },
  { border: "border-border/60", duration: "duration-500" },
  { border: "border-border/50", duration: "duration-700" },
  { border: "border-border/30", duration: "" },
];

const CreateCampaignButton = () => {
  const navigate = useNavigate();

  const rings = RINGS.reduceRight<ReactNode>(
    (inner, ring, i) => {
      const innermost = i === RINGS.length - 1;
      return (
        <div
          style={{ borderRadius: `max(calc(2px * var(--radius-on)), calc(var(--radius-2xl) - var(--gap) * ${i + 1}))` }}
          className={cn(
            "flex size-full items-center justify-center gap-2 border border-dashed",
            ring.border,
            innermost
              ? "text-muted-foreground transition-colors group-hover:text-foreground"
              : cn("p-(--gap) transition-all", ring.duration),
          )}
        >
          {inner}
        </div>
      );
    },
    <>
      <Plus className="transition-all duration-500 group-hover:rotate-180" />
      <span>Create campaign</span>
    </>,
  );

  return (
    <Button
      onClick={() => navigate("/dashboard/campaigns/create")}
      className="group relative h-48 cursor-pointer rounded-2xl border border-dashed p-(--gap) transition-all [--gap:--spacing(2)] hover:bg-sidebar/10 hover:[--gap:--spacing(3)]"
      variant="outline"
      card={true}
    >
      {rings}
    </Button>
  );
};